import { type FC, useCallback, useEffect, useRef } from 'react';

import { useViewer } from '@/features/document-viewer/use-viewer';
import { useViewerContext } from '@/features/document-viewer/use-viewer-context';
import { useAgentChatViewport } from '@/layouts/use-agent-chat-viewport';

import { AgentChatDrawer } from './agent-chat-drawer';

/**
 * Mounts the drawer at the package route rather than inside the workspace tabs,
 * so switching tabs no longer tears down the conversation, the draft question,
 * or the scroll position — and no longer replays the open animation.
 */
export const AgentChatHost: FC = () => {
  const {
    agentChatWidth,
    closeAgentChat,
    isAgentChatOpen,
    setAgentChatWidth,
    setHasAgentChatEntered,
    setIsAgentChatMaximized,
    setIsAgentChatOpen,
    setIsAgentChatResizing,
  } = useAgentChatViewport();
  const { close: closeViewer } = useViewer();
  const { isStandaloneDocument } = useViewerContext();

  /**
   * A document opened from a citation belongs to the chat, so it goes when the
   * chat goes. Leaving it behind strands ViewerMode on DocumentFullscreen with
   * the drawer gone, and the next tab click re-opens that document on its own. A
   * document the user opened from a list is left alone.
   *
   * Held in a ref so the teardown below depends on nothing that changes — an
   * unmount effect that re-runs would close the drawer mid-session.
   */
  const dismissCitationViewerRef = useRef(() => undefined as void);

  useEffect(() => {
    dismissCitationViewerRef.current = () => {
      if (isStandaloneDocument) closeViewer();
    };
  }, [closeViewer, isStandaloneDocument]);

  const finishOpeningAgentChat = useCallback(
    () => setHasAgentChatEntered(true),
    [setHasAgentChatEntered],
  );

  const closeAgentChatAndCitation = useCallback(() => {
    dismissCitationViewerRef.current();
    closeAgentChat();
  }, [closeAgentChat]);

  // Leaving the package closes the chat: the next package gets its own
  // conversation, so carrying an open drawer across would be misleading.
  useEffect(
    () => () => {
      dismissCitationViewerRef.current();
      setIsAgentChatOpen(false);
      setIsAgentChatMaximized(false);
      setHasAgentChatEntered(false);
    },
    [setHasAgentChatEntered, setIsAgentChatMaximized, setIsAgentChatOpen],
  );

  if (!isAgentChatOpen) return null;

  return (
    <AgentChatDrawer
      width={agentChatWidth}
      onClose={closeAgentChatAndCitation}
      onEntered={finishOpeningAgentChat}
      onMaximizedChange={setIsAgentChatMaximized}
      onResizingChange={setIsAgentChatResizing}
      onWidthChange={setAgentChatWidth}
    />
  );
};
